import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from '@firebase/auth';
import { auth } from '../firebase';
import { Navigate } from 'react-router-dom';
import { getUserMe } from '../utils/secureFunctions';

interface Props {
  children: React.ReactNode;
  roles?: Array<'admin' | 'moderator' | 'super_admin'>; // optional role gating
}

export default function ProtectedRoute({ children, roles }: Props): React.ReactElement | null {
  const [user, setUser] = useState<any | null | undefined>(undefined);
  const [roleAllowed, setRoleAllowed] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u && roles && roles.length) {
        try {
          const token = await u.getIdTokenResult();
          const claims: any = token?.claims || {};
          const role = String(claims.role || '').toLowerCase();

          const userRoles: string[] = [];
          if (claims.admin === true || role === 'admin' || role === 'super_admin') userRoles.push('admin');
          if (role === 'moderator' || claims.moderator === true) userRoles.push('moderator');
          if (role === 'super_admin' || claims.super_admin === true) userRoles.push('super_admin');

          if (!roles.some((r) => userRoles.includes(r))) {
            // Roles are stored server-side; token claims are only a fast path.
            const me = await getUserMe().catch(() => null);
            const dbRole = String((me?.profile as { role?: string } | undefined)?.role || '').toLowerCase();
            if (dbRole) userRoles.push(dbRole);
          }
          setRoleAllowed(roles.some((r) => userRoles.includes(r)));
        } catch {
          setRoleAllowed(false);
        }
      } else if (roles && roles.length) {
        setRoleAllowed(false);
      } else {
        setRoleAllowed(true);
      }
    });
    return () => unsub();
  }, [roles]);

  if (user === undefined) {
    // Minimal app-shell skeleton to avoid archaic loading text
    return (
      <div style={{ minHeight: '100vh', background: '#F5EDE1' }}>
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 60, background: '#fff', borderBottom: '1px solid #eee', zIndex: 1000 }}>
          <div style={{ height: 20, width: 140, margin: '20px var(--spacing-base)', borderRadius: 6, background: 'linear-gradient(90deg, #eee 25%, #f6f6f6 37%, #eee 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.2s infinite' }} />
        </div>
        <div style={{ height: 60 }} />
        <div style={{ maxWidth: 800, margin: '24px auto', padding: '0 20px' }}>
          <div style={{ height: 180, borderRadius: 12, background: 'linear-gradient(90deg, #eee 25%, #f6f6f6 37%, #eee 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.2s infinite' }} />
          <div style={{ height: 16, width: '70%', marginTop: 14, borderRadius: 6, background: 'linear-gradient(90deg, #eee 25%, #f6f6f6 37%, #eee 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.2s infinite' }} />
          <div style={{ height: 16, width: '50%', marginTop: 8, borderRadius: 6, background: 'linear-gradient(90deg, #eee 25%, #f6f6f6 37%, #eee 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.2s infinite' }} />
        </div>
        <style>{`@keyframes shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}`}</style>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && roles.length) {
    if (roleAllowed === undefined) return null; // still resolving
    if (!roleAllowed) return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}
